import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'

/**
 * Shift patterns — the templates behind Settings → Shift Master.
 *
 * ── A pattern is not a shift ─────────────────────────────────────────────
 * It is the TEMPLATE a shift is an occurrence of: "Night, 22:00–06:00, Mon–Sun,
 * at Findlay". The roster in `features/shifts` is the other thing — the shifts
 * actually worked, by whom, on which line — and the two sit at the same URL
 * because the API mounted the real resource where the demo roster already was.
 * Same collision as `/v1/users`, resolved the same way: by mode, not by prefix.
 * See endpoints.js.
 *
 * ── What the patterns are FOR ───────────────────────────────────────────
 * Resolving an operator's login to a shift, which then pre-fills Log Event and
 * Shift Handover. That is why the screen's subject is not really the list — it
 * is the two things that go wrong with one: two patterns claiming the same
 * minute, and a pattern named for hours it does not run.
 *
 * ── A pattern belongs to ONE plant, or to the account ───────────────────
 * `plant_id` set is that plant's own shift; `plant_id: null` is an ACCOUNT-LEVEL
 * shift applying to every plant. Null is a real value meaning "everywhere", not
 * a field left unset — anything reading it as "no plant" gets the common case
 * backwards.
 *
 * This replaced `plant_ids: [uuid]`, a LIST, which is what this module sent
 * until the `shift_scopes` table was dropped (backend `e1eb1af`, 5 Oct 2026).
 * Three things changed with it and each one is load-bearing:
 *
 *   one plant, not many — there is no longer any way to say "these three
 *   plants"; it is one plant or the whole account.
 *
 *   `plant_id` is CREATE-ONLY. `ShiftUpdate` does not declare it, so a PUT
 *   carrying it is refused. Which plant a shift belongs to is a fact fixed when
 *   it is made, like a handover's shift. See `toUpdateWire`.
 *
 *   `is_active` is the mirror: UPDATE-ONLY. `ShiftCreate` does not declare it,
 *   because a pattern is created in service and retired later. The status
 *   checkbox therefore appears on an edit and not on an add.
 *
 *   the field is now rejected rather than ignored. The API 400s on any field a
 *   schema does not declare (backend `8107a70`), so sending the old `plant_ids`
 *   failed both writes outright — it was not a silently-dropped extra.
 *
 * ── Who may do what, which the screen mirrors ───────────────────────────
 * An ACCOUNT-level `shifts.manage` grant sees, creates, edits and deletes
 * everything. A PLANT-level grant does all of that for its own plants' shifts
 * and sees account-level ones READ-ONLY — creating, editing or deleting one is a
 * 403. `covers_plant` in the backend is the single rule; `usePermissionScopes`
 * is how this client asks the same question.
 *
 * ── `type` replaced the design's icon and colour picker ─────────────────
 * The prototype stores `icon: "sun"` and `col: "#F59E0B"` per pattern, and the
 * demo build carried an icon vocabulary and a tone list to match. The API has
 * neither — but it has `type`, a six-value enum that is what those two were
 * standing in for. So the look is DERIVED from the type below rather than
 * stored, which is strictly better than the design: a Night shift cannot be
 * given the Day shift's colour by accident, and no hex goes anywhere near a
 * component. The pickers are commented out in the drawer, not deleted.
 */

/**
 * `type` → how the card and chip read.
 *
 * Tone ids from `lib/tone.js`, never a hex — a literal ignores `data-theme` and
 * colour-blind mode, and the single-palette rule forbids it outright.
 *
 * Violet is night, which is what `SHIFT_TYPE_TONE` in the roster module already
 * says, so the two screens agree about what a night shift looks like.
 */
export const TYPE_LOOK = {
  day: { icon: 'sun', tone: 'amber' },
  morning: { icon: 'sun', tone: 'green' },
  evening: { icon: 'gauge', tone: 'forge' },
  night: { icon: 'moon', tone: 'violet' },
  general: { icon: 'cal', tone: 'ack' },
  rotating: { icon: 'gear', tone: 'watch' },
}

/** The enum, in the order the form offers it. */
export const SHIFT_TYPES = ['day', 'morning', 'evening', 'night', 'general', 'rotating']

/**
 * Degrade rather than break on a type this build has never heard of.
 *
 * The enum is a Postgres type, so a seventh value means a backend migration —
 * and the two repositories ship on separate branches, so the server will have it
 * first. Without this, that day renders an empty square where the icon is.
 */
export const lookFor = (type) => TYPE_LOOK[type] ?? { icon: 'cal', tone: 'ack' }

/**
 * Weekdays: `mon`…`sun` on the wire, `Date#getDay` numbers in the app.
 *
 * The app counts days as the platform does, because `resolveCurrentPattern`
 * compares against `new Date().getDay()` and `(day + 6) % 7` has to mean
 * yesterday. Translated here and in no other place.
 */
const WIRE_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const toDayNumber = (wire) => WIRE_DAYS.indexOf(wire)
const toWireDay = (day) => WIRE_DAYS[day]

/**
 * `"22:00:00"` → `"22:00"`.
 *
 * The API serves a `time`, which serialises with seconds. `<input type="time">`
 * shows and emits `HH:MM`, and every comparison in `arithmetic.js` is on that
 * shape, so the seconds are dropped once here rather than tolerated everywhere.
 */
const toClock = (value) => (value ?? '').slice(0, 5)

/** Back the other way. The API accepts `HH:MM`; seconds are sent for symmetry. */
const toWireTime = (clock) => (clock.length === 5 ? `${clock}:00` : clock)

/** Wire → app. */
const toPattern = (row) => ({
  id: row.id,
  name: row.name,
  type: row.type,
  start: toClock(row.start_time),
  end: toClock(row.end_time),
  days: (row.week_days ?? []).map(toDayNumber).filter((day) => day >= 0),
  /* `null` is every plant — an account-level shift. See the header. */
  plantId: row.plant_id ?? null,
  active: row.is_active === true,
})

/**
 * App → wire, the six fields both writes share.
 *
 * Every field on every write: `ShiftCreate` and `ShiftUpdate` give none of them a
 * default, so this is a full replace either way and a key left out is a refusal
 * rather than "leave it alone".
 *
 * `week_days` has `min_length=1` — a pattern that runs on no days is refused by
 * the server, and the form refuses it first so the answer arrives beside the
 * control rather than as a banner.
 */
const toFields = (pattern) => ({
  name: pattern.name.trim(),
  type: pattern.type,
  start_time: toWireTime(pattern.start),
  end_time: toWireTime(pattern.end),
  /* Sorted into the week's own order. The set is what matters to the server, but
     an ordered list is what a human reads in a diff or a log. */
  week_days: [...pattern.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map(toWireDay),
})

/**
 * Create adds the one field update may not carry.
 *
 * `plant_id` is required and nullable: the key must be PRESENT with `null` for an
 * account-level shift, which is the usual nullable-means-required rule this API
 * follows everywhere.
 */
const toCreateWire = (pattern) => ({ ...toFields(pattern), plant_id: pattern.plantId ?? null })

/**
 * Update adds the field create may not carry.
 *
 * `is_active` is the mirror image of `plant_id`: `ShiftCreate` does not declare
 * it and `ShiftUpdate` does. A pattern is created in service and retired later,
 * which is the same rule the asset hierarchy follows — see
 * `features/hierarchy/api.js` `toBody`. Sending it on a create is a 400.
 *
 * `plant_id` stays out, for the reason below.
 */

/**
 * Update sends the six fields and nothing else.
 *
 * `plant_id` is deliberately absent, not defaulted: `ShiftUpdate` does not
 * declare it and the API 400s on undeclared fields. Spreading the pattern here
 * would put it back and break every save.
 */
const toUpdateWire = (pattern) => ({ ...toFields(pattern), is_active: pattern.active })

/**
 * Every pattern in the account.
 *
 * Asked at the page-size ceiling and filtered in the client, which is the
 * opposite of what the directory does and is right here for two reasons the
 * screen depends on:
 *
 *   Overlap detection is a question about the WHOLE set. A pattern conflicts
 *   with one on another page just as much, and a warning that appears only when
 *   both happen to be on screen is worse than none.
 *
 *   So is resolving the current shift. Both live in `arithmetic.js` and both
 *   take the list.
 *
 * An account with more than 200 shift patterns is not a thing — there are six
 * types and a handful of plants. `truncated` says so out loud rather than
 * quietly showing a subset, the same admission the asset-class library makes.
 */
export async function fetchPatterns(signal) {
  const data = await get(endpoints.shifts.patterns, {
    params: { page: 1, page_size: 200, sort_by: 'name', sort_dir: 'asc' },
    signal,
  })

  const total = data?.total ?? 0
  const items = (data?.shifts ?? []).map(toPattern)

  return { items, total, truncated: total > items.length }
}

/**
 * Create one.
 *
 * The server does NOT check for overlaps — two patterns may claim the same
 * minute at the same plant and it will store both. The screen warns about it
 * and still allows the save, deliberately: an overlap is occasionally what
 * somebody means (a handover window), and the resolution rule is defined —
 * the narrowest window wins. Recorded in BACKEND-GAPS.md, because a warning
 * only this client shows is not a constraint.
 *
 * Refusals come back field-keyed: `plant_id` for a plant this account does not
 * own or that the caller's grant does not cover, `week_days` for an empty list.
 * Asking for an account-level shift (`plant_id: null`) without an account-level
 * grant is a 403 rather than a field error, because it is about the caller and
 * not about the value.
 */
export async function createPattern(pattern) {
  return toPattern(await post(endpoints.shifts.patterns, toCreateWire(pattern)))
}

/**
 * Update one. Full replace of the six editable fields — see `toUpdateWire`.
 *
 * The plant cannot be changed here, by anybody: moving a shift between plants is
 * not an edit the API offers. The drawer locks the control instead of sending a
 * value the server would reject.
 */
export async function updatePattern(pattern) {
  return toPattern(await put(endpoints.shifts.pattern(pattern.id), toUpdateWire(pattern)))
}

/**
 * Delete one.
 *
 * Soft on the server. Not refused for a pattern anybody is rostered against,
 * which is worth knowing: `UserShift` rows point at it and keep pointing after
 * it stops listing. Deactivating (`is_active: false`) is the reversible act and
 * is what the drawer offers beside it — turning a pattern off is how you retire
 * it without touching what it already explains.
 */
export async function deletePattern(id) {
  await del(endpoints.shifts.pattern(id))
  return { id, deleted: true }
}
